
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { postSupplier } from "../../app/services/supplierService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";

import Form from "react-bootstrap/Form";
import { Alert, Breadcrumb, Button, Col, Row } from "react-bootstrap";
import { Supplier } from "../../models/responses/supplier";
import { useSelector } from "react-redux";
import { Warehouse } from "../../models/warehouse/warehouse";
import { RootState } from "../../app/store";

interface Props { }

const SupplierAdd: FC<Props> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [displayError, setDisplayError] = useState(false);
  const isWaitingSupplierPostResponse = useSelector(
    (state: RootState) => state.supplier.isWaitingSupplierPostResponse,
  );
  const [supplierData, setSupplierData] = useState<Supplier>({
    id: "",
    externalId: "",
    name: "",
    brand: "",
    percent: "",
    pickupName: "",
    pickupAddress: "",
    address: "",
    email: "",
    phone: "",
    comment: "",
    createdAt: "",
    currency: "",
    isExcludedFromExport: false,
    deletedAt: null,
    updatedAt: null,
    supplierHistory: null,
    clientTypeStandard: false,
    clientTypeCatalog: false,
    clientTypeFleet: false,
    warehouse: "",
  });

  const externalIdInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, externalId: e.target.value });
  };

  const brandInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, brand: e.target.value });
  };

  const nameInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, name: e.target.value });
  };

  const percentageInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, percent: e.target.value });
  };

  const pickupNameInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, pickupName: e.target.value });
  };

  const pickupAddressInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSupplierData({ ...supplierData, pickupAddress: e.target.value });
  };

  const addressInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, address: e.target.value });
  };

  const emailInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, email: e.target.value });
  };

  const phoneInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, phone: e.target.value });
  };

  const commentInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, comment: e.target.value });
  };

  const currencyInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, currency: e.target.value });
  };

  const isExcludedFromExportHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSupplierData({
      ...supplierData,
      isExcludedFromExport: e.target.checked,
    });
  };

  const clientTypeStandardHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSupplierData({ ...supplierData, clientTypeStandard: e.target.checked });
  };

  const clientTypeCatalogHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, clientTypeCatalog: e.target.checked });
  };

  const clientTypeFleetHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierData({ ...supplierData, clientTypeFleet: e.target.checked });
  };

  const warehouseInputHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSupplierData({ ...supplierData, warehouse: e.target.value });
  };

  const handleAddSupplier = () => {
    setDisplayError(false);
    dispatch(postSupplier(instance, account, supplierData, onSuccess));
  };

  const onSuccess = (supplierId: string | null) => {
    if (supplierId === null) {
      setDisplayError(true);
      return;
    }

    setTimeout(() => {
      navigate(`../${supplierId}`, { relative: "path" });
    }, 500);
  };

  return (
    <div>
      <Breadcrumb>
        <Breadcrumb.Item href="/suppliers">Leverantörer</Breadcrumb.Item>
        <Breadcrumb.Item active>Ny leverantör</Breadcrumb.Item>
      </Breadcrumb>
      <hr />

      <Form className="w-3">
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Leverantör *</Form.Label>
              <Form.Control
                type="text"
                defaultValue={supplierData.name ?? ""}
                onInput={nameInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Leverantörsnummer (AX)</Form.Label>
              <Form.Control
                type="text"
                placeholder="AX0000"
                defaultValue={supplierData.externalId ?? ""}
                onInput={externalIdInputHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Bilmärke</Form.Label>
              <Form.Control
                type="text"
                defaultValue={supplierData.brand ?? ""}
                onInput={brandInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Procentsats</Form.Label>
              <Form.Control
                type="text"
                defaultValue={supplierData.percent ?? ""}
                onInput={percentageInputHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Hämtningsinformation</Form.Label>
              <Form.Control
                type="text"
                defaultValue={supplierData.pickupName ?? ""}
                onInput={pickupNameInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Hämtningsaddress</Form.Label>
              <Form.Control
                type="text"
                defaultValue={supplierData.pickupName ?? ""}
                onInput={pickupAddressInputHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Adress</Form.Label>
              <Form.Control
                type="text"
                defaultValue={supplierData.address}
                onInput={addressInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>E-post</Form.Label>
              <Form.Control
                type="email"
                defaultValue={supplierData.email}
                onInput={emailInputHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Telefonnummer</Form.Label>
              <Form.Control
                type="text"
                defaultValue={supplierData.phone}
                onInput={phoneInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Valuta</Form.Label>
              <Form.Control
                type="text"
                defaultValue={supplierData.currency}
                onInput={currencyInputHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>AX order lagerställe <span style={{ color: "red" }}>(ändra endast vid särskilda behov)</span></Form.Label>
              <Form.Select
                value={supplierData.warehouse || ""}
                onChange={warehouseInputHandler}
              >
                <option value="">------</option>
                {Warehouse.values().map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Anteckning</Form.Label>
              <Form.Control
                as="textarea"
                defaultValue={supplierData.comment ?? ""}
                onInput={commentInputHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Ingår ej i orderexport till AX</Form.Label>
              <Form.Check
                type="checkbox"
                checked={supplierData.isExcludedFromExport ?? false}
                onChange={isExcludedFromExportHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Skadeverkstad</Form.Label>
              <Form.Check
                type="checkbox"
                checked={supplierData.clientTypeStandard ?? false}
                onChange={clientTypeStandardHandler}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>AD Butik</Form.Label>
              <Form.Check
                type="checkbox"
                checked={supplierData.clientTypeCatalog ?? false}
                onChange={clientTypeCatalogHandler}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Fleet</Form.Label>
              <Form.Check
                type="checkbox"
                checked={supplierData.clientTypeFleet ?? false}
                onChange={clientTypeFleetHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        <div className="text-end">
          <Button
            variant="primary"
            className="mb-3"
            onClick={handleAddSupplier}
            disabled={
              !(
                supplierData.name !== undefined &&
                supplierData.name.length > 0 &&
                !isWaitingSupplierPostResponse
              )
            }
          >
            Lägg till leverantör
          </Button>
        </div>

        <Alert key="alert" variant="danger" show={displayError}>
          <p>Något gick fel, kontrollera uppgifterna och försök igen.</p>
        </Alert>
      </Form>
    </div>
  );
};

export default SupplierAdd;
